import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import * as _ from "lodash";
import * as moment from "moment-timezone";
import SortOptions from "../../../../models/content-wrapper/sort-options";
import CompanyResourceType from "../../../../models/resource-management/company-resource-type.enum";
import SelectFormInput from "../../../../models/SelectFormInput";
import { ResourceManagementService } from "../../../../providers/resource-management/resource-management.service";
import { UtilityService } from "../../../../providers/utility-service/utility-service";
import { OrderForm } from "../order-form/order-form";

@Component({
  selector: "other-order-form",
  templateUrl: "other-order-form.html",
})
export class OtherOrderFormComponent extends OrderForm implements OnInit {
  protected incomingData: any = {};
  protected orderForm: FormGroup;
  private textAreaMaxLength = 1000;
  private textMaxLength = 100;
  private startDateMin: string = "";
  private startDateMax: string = "";
  private endDateMin: string = "";
  private endDateMax: string = "";
  defaultStartTime: string = "";

  hidePhysiciansOptions: Boolean = true;
  searchTerm: string = "";
  selectPhysicianFlag: boolean = false;
  setPhysicianName: boolean = false;

  hideDcPhysiciansOptions: Boolean = true;
  dcSearchTerm: string = "";
  selectDcPhysicianFlag: boolean = false;
  setDcPhysicianName: boolean = false;

  private physicians: any = [];

  @Input() physicianOptions: string[] = [];
  @Input() physicianOrderOptions: SelectFormInput[] = [];
  @Input() status: any;
  @Input() isOpenOrderEdit: boolean = false;
  @Input() isActiveOrderEdit: boolean = false;
  @Input() isIpad: boolean = false;
  @Output() onValidityChange: EventEmitter<any> = new EventEmitter();

  constructor(
    private formBuilder: FormBuilder,
    private resourceManagementService: ResourceManagementService,
    public utilityService: UtilityService
  ) {
    super();
    this.startDateMin = moment().subtract("10", "years").format("YYYY-MM-DD");
    this.startDateMax = moment().add("10", "years").format("YYYY-MM-DD");
    this.endDateMin = moment().subtract("10", "years").format("YYYY-MM-DD");
    this.endDateMax = moment().add("10", "years").format("YYYY-MM-DD");
    this.defaultStartTime = moment().format("YYYY-MM-DD");

    this.orderForm = this.formBuilder.group({
      name: [
        "",
        Validators.compose([
          Validators.required,
          Validators.maxLength(this.textMaxLength),
        ]),
      ],
      physician: ["", Validators.compose([Validators.required])],
      itemStartDatetime: ["", Validators.compose([Validators.required])],
      itemDiscontinueDatetime: [""],
      acdApproval: [false],
      description: [
        "",
        Validators.compose([Validators.maxLength(this.textAreaMaxLength)]),
      ],
      physicianOrder: ["", Validators.compose([Validators.required])],
      physicianOrderNotes: [
        "",
        Validators.compose([Validators.maxLength(this.textAreaMaxLength / 2)]),
      ],
      searchTerm: new FormControl(""),
      dcSearchTerm: new FormControl(""),
    });

    this.orderForm.valueChanges.subscribe((data) => {
      console.log(data, this.orderForm.valid);
      this.onValidityChange.emit({
        validity: this.orderForm.valid,
        data: data,
      });
    });

    //enforce character limit
    this.orderForm.controls["name"].valueChanges.subscribe((data) => {
      if (data.length > this.textMaxLength) {
        this.orderForm.controls["name"].setValue(
          data.substring(0, this.textMaxLength)
        );
      }
    });

    //enforce character limit
    this.orderForm.controls["description"].valueChanges.subscribe((data) => {
      if (data.length > this.textAreaMaxLength) {
        this.orderForm.controls["description"].setValue(
          data.substring(0, this.textAreaMaxLength)
        );
      }
    });

    //enforce character limit
    this.orderForm.controls["physicianOrderNotes"].valueChanges.subscribe(
      (data) => {
        if (data.length > this.textAreaMaxLength / 2) {
          this.orderForm.controls["physicianOrderNotes"].setValue(
            data.substring(0, this.textAreaMaxLength / 2)
          );
        }
      }
    );

    //enforce startDate cannot be after endDate
    this.orderForm.controls["itemStartDatetime"].valueChanges.subscribe(
      (data) => {
        let startDate = moment(data).format("YYYY-MM-DD");
        let endValue = this.orderForm.controls["itemDiscontinueDatetime"].value;

        if (endValue.length > 0) {
          let endDate = moment(endValue).format("YYYY-MM-DD");
          if (moment(startDate).isAfter(endDate)) {
            this.orderForm.controls["itemStartDatetime"].setValue("");
          }
        }
        this.endDateMin = moment(data).format("YYYY-MM-DD");
      }
    );

    //enforce endDate cannot be before startDate
    this.orderForm.controls["itemDiscontinueDatetime"].valueChanges.subscribe(
      (data) => {
        let startValue = this.orderForm.controls["itemStartDatetime"].value;
        let endDate = moment(data).format("YYYY-MM-DD");

        if (startValue.length > 0) {
          let startDate = moment(startValue).format("YYYY-MM-DD");
          if (moment(startDate).isAfter(endDate)) {
            this.orderForm.controls["itemDiscontinueDatetime"].setValue("");
          }
        }
        this.startDateMax = moment(data).format("YYYY-MM-DD");
      }
    );
  }

  ngOnInit() {
    if (this.incomingData) {
      if (this.incomingData.rmPhysician) {
        this.searchTerm = this.incomingData.rmPhysician.displayName;
      }

      //apply limits to prevent startDate > endDate if data exists
      let startDatetime = this.incomingData.itemStartDatetime;
      if (startDatetime) {
        this.endDateMin = moment(startDatetime).format("YYYY-MM-DD");
      }
      let endDatetime = this.incomingData.itemDiscontinueDatetime;
      if (endDatetime) {
        this.startDateMax = moment(endDatetime).format("YYYY-MM-DD");
      }
    }
    // if discontinue make itemDiscontinueDatetime required
    if (this.status === "discontinue") {
      let discDate = this.orderForm.get("itemDiscontinueDatetime");
      discDate.setValidators([Validators.required]);
      discDate.updateValueAndValidity();
      this.orderForm.addControl(
        "dcPhysician",
        new FormControl("", Validators.required)
      );
      this.orderForm.controls["dcPhysician"].updateValueAndValidity();
    }
  }

  /**
   * @param searchTerm user enters to search the physician, when the term length is > 2 at that time call that getPhysicians api
   */
  searchPhysicians(searchTerm: string) {
    if (searchTerm !== "") {
      this.hidePhysiciansOptions = false;
      if (searchTerm.length > 2) {
        this.getPhysicians(searchTerm);
      }
    }
  }

  /**
   * @param searchTerm search term to get physicians
   */
  getPhysicians(searchTerm) {
    if (!this.selectPhysicianFlag) {
      this.selectPhysicianFlag = false;
      this.setPhysicianName = true;
      this.resourceManagementService
        .getCompanyResourceSummaries(
          0,
          10000,
          searchTerm,
          new SortOptions("last_name", "asc"),
          CompanyResourceType.PROVIDER
        )
        .subscribe(
          (response) => {
            this.physicians = [];
            let providers = _.filter(response.content, (x) => x.externalId); // valid external Id.
            this.physicians = this.physicians.concat(providers);
          },
          (err) => {
            this.utilityService.presentErrorToast(
              err,
              "Failed to Load Providers"
            );
          }
        );
    } else {
      this.selectPhysicianFlag = false;
    }
  }

  setPhysician(physician: any) {
    this.selectPhysicianFlag = true;
    this.setPhysicianName = false;
    this.hidePhysiciansOptions = true;
    this.orderForm.controls["physician"].setValue(physician);
    this.orderForm.controls["searchTerm"].setValue(physician.displayName);
  }
  /**
   * @param searchTerm user enters to search the physician, when the term length is > 2 at that time call that getPhysicians api
   */
  searchDcPhysicians(searchTerm: string) {
    if (searchTerm !== "") {
      this.hideDcPhysiciansOptions = false;
      if (searchTerm.length > 2) {
        this.getDcPhysicians(searchTerm);
      }
    }
  }

  /**
   * @param searchTerm search term to get physicians
   */
  getDcPhysicians(searchTerm) {
    if (!this.selectDcPhysicianFlag) {
      this.selectDcPhysicianFlag = false;
      this.setDcPhysicianName = true;
      this.resourceManagementService
        .getCompanyResourceSummaries(
          0,
          10000,
          searchTerm,
          new SortOptions("last_name", "asc"),
          CompanyResourceType.PROVIDER
        )
        .subscribe(
          (response) => {
            this.physicians = [];
            let providers = _.filter(response.content, (x) => x.externalId); // valid external Id.
            this.physicians = this.physicians.concat(providers);
          },
          (err) => {
            this.utilityService.presentErrorToast(
              err,
              "Failed to Load Providers"
            );
          }
        );
    } else {
      this.selectDcPhysicianFlag = false;
    }
  }

  setDcPhysician(physician: any) {
    this.selectDcPhysicianFlag = true;
    this.setDcPhysicianName = false;
    this.hideDcPhysiciansOptions = true;
    this.orderForm.controls["dcPhysician"].setValue(physician);
    this.orderForm.controls["dcSearchTerm"].setValue(physician.displayName);
  }
}



// WEBPACK FOOTER //
// ./src/components/orders/forms/other-order-form/other-order-form.ts